import { HTMLAttributes, ReactNode } from 'react';

export namespace LocationTypes {
  export interface ILocationMap extends HTMLAttributes<HTMLDivElement> {
    locations: TLocation[];
    showNearbyBusiness: Boolean; //default: false - Will implement this later,
    pinIcon?: React.ComponentType<TPinIcon> | IconType;
    googleMapAPIKey: string;
    height: number;
    width: number | string;
    fullWidth?: boolean;
    infoWindowmode: TModes;
    variant: TVariantModes;
    zoomLevel?: number;
    custom?: React.ComponentType<TPopOver>;
    defaultCoordinates?: TCoords;
    onLocationsUpdated?: React.Dispatch<
      React.SetStateAction<LocationTypes.ILocationMap>
    >;
    useIframe?: boolean;
  }

  export interface ILocationPin extends HTMLAttributes<ReactNode> {
    lat: number;
    lng: number;
    showPopOver?: boolean;
    pinIcon: React.ComponentType<TPinIcon> | IconType;
    popOver: React.ComponentType<TPopOver>;
    curLocation: TLocation;
    allLocations?: TLocation[];
    curOpenLocationId: string;
    onClick: React.MouseEventHandler<HTMLDivElement>;
    onGetFormattedHrs: (
      location: TLocation
    ) => {
      officeHrs: string[];
      gateHrs: string[];
    };
  }

  export interface ILocationHours extends HTMLAttributes<HTMLDivElement> {
    facilityHours: string[];
  }

  export interface IDefaultPin extends HTMLAttributes<HTMLDivElement> {
    facilityAddress?: string;
  }

  export interface IContactTile extends HTMLAttributes<HTMLDivElement> {
    facilityName: string;
    facilityAddress: string;
    googleReviewRating?: TReviewRating;
    lat?: number; // Required by react-map
    lng?: number; // Required by react map
  }

  export interface IInfoWindow extends HTMLAttributes<HTMLDivElement> {
    facilityName: string;
    facilityAddress: string;
    googleReviewRating?: TReviewRating;
    googleMapPlaceId: string;
    nextOpenOrCloseHrs: string;
    curFacilityStatus: 'open' | 'close';
    contactNumber: string;
    formattedHrs?: TFormattedFacilityHours;
    gMapDetails?: TGMapLocation;
  }

  export interface ILocationCard extends HTMLAttributes<HTMLDivElement> {
    location: TLocation;
    getFormattedHrs: (
      location: LocationTypes.TLocation
    ) => {
      officeHrs: string[];
      gateHrs: string[];
    };
  }
  export interface ILocationTile extends HTMLAttributes<HTMLDivElement> {
    locations: TLocation[];
    onNotificationMessage: React.Dispatch<React.SetStateAction<string>>;
    curOpenLocation: string;
  }

  export interface ICoverVariant extends HTMLAttributes<HTMLDivElement> {
    renderMap: () => JSX.Element;
  }

  export interface IMapMultiLocationVariant
    extends HTMLAttributes<HTMLDivElement> {
    renderMap: () => JSX.Element;
    locations: LocationTypes.TLocation[];
    getFormattedHrs: TGetFormattedHrs;
  }

  export interface IMapMultiOneVariant extends HTMLAttributes<HTMLDivElement> {
    setNotificationMsg: (value: React.SetStateAction<string>) => void;
    notificationMsg: string;
    renderMap: () => JSX.Element;
    locations: LocationTypes.TLocation[];
    curOpenLocation: string;
  }
  export type TTheme = 'light' | 'dark';
  export interface IFacilityHrs extends HTMLAttributes<HTMLDivElement> {
    show: boolean;
    onFacilityHrsToggle: () => void;
    formattedHrs: TFormattedFacilityHours;
    theme?: TTheme;
  }

  export interface IFormattedHrs extends HTMLAttributes<HTMLDivElement> {
    onFacilityHrsToggle?: () => void;
    formattedHrs: TFormattedFacilityHours;
    hideToggle?: boolean;
    show?: 'facilityHrsOnly' | 'gateHrsOnly' | 'both';
    hideTitles?: boolean;
    theme?: TTheme;
    color?: string;
    className?: string;
    titleClassName?: string;
    size?: number;
  }

  export type TGetFormattedHrs = (
    location: LocationTypes.TLocation
  ) => {
    officeHrs: string[];
    gateHrs: string[];
  };

  export type TModes =
    | 'location'
    | 'contact'
    | 'pinOnly'
    | 'custom'
    | 'default';

  export type TReviewRating = {
    rating?: number;
    totalReviews?: number;
  };

  export type TCoords = {
    lat: number;
    lng: number;
  };

  export type TOpenCloseHrs = {
    open: string;
    close: string;
  };

  export type TCustomHours = {
    sunday: TOpenCloseHrs;
    monday: TOpenCloseHrs;
    tuesday: TOpenCloseHrs;
    wednesday: TOpenCloseHrs;
    thursday: TOpenCloseHrs;
    friday: TOpenCloseHrs;
    saturday: TOpenCloseHrs;
  };
  export type TWeekendHours = {
    sunday: TOpenCloseHrs;
    saturday: TOpenCloseHrs;
  };
  export type TWeekdayHours = {
    monday: TOpenCloseHrs;
    tuesday: TOpenCloseHrs;
    wednesday: TOpenCloseHrs;
    thursday: TOpenCloseHrs;
    friday: TOpenCloseHrs;
  };

  export type TDaywiseHours = {
    allDays?: TOpenCloseHrs;
    weekends?: TOpenCloseHrs;
    weekdays?: TOpenCloseHrs;
    saturdayOnly?: TOpenCloseHrs;
    sundayOnly?: TOpenCloseHrs;
    notProvided?: TOpenCloseHrs;
  };

  export type TFacilityHours = {
    officeHrs: TDaywiseHours;
    gateHrs: TDaywiseHours;
  };

  export type TFormattedFacilityHours = {
    officeHrs: string[];
    gateHrs: string[];
  };

  export type TGMapHourPeriods = {
    close: {
      day: number;
      time: string;
    };
    open: {
      day: number;
      time: string;
    };
  };

  export type TGMapOpeningHrs = {
    open_now: boolean;
    periods: TGMapHourPeriods[];
    weekday_text: string[];
  };

  export type TGMapLocation = {
    formatted_address: string;
    geometry: {
      location: TCoords;
      viewport?: {
        northeast?: TCoords;
        southwest?: TCoords;
      };
    };
    opening_hours?: TGMapOpeningHrs;
    name: string;
    rating?: number;
    user_ratings_total?: number;
  };

  export type TLocation = {
    addressText: string;
    mailingAddressText?: string;
    facilityName: string;
    hours: TFacilityHours;
    unitStartingPrice: number;
    contactNumber: string;
    emailAddress: string;
    googleMapPlaceId: string;
    useGoogleMapsContactDetails: boolean;
    gMapDetails?: TGMapLocation;
    thumbnail?: string;
    iFrameUrl?: string;
    locationUrl?: string;
  };

  export type TPopOver = {
    facilityName: string;
    facilityAddress: string;
    googleReviewRating?: TReviewRating;
    googleMapPlaceId: string;
    nextOpenOrCloseHrs: string;
    curFacilityStatus: 'open' | 'close';
    contactNumber: string;
    formattedHrs?: TFormattedFacilityHours;
    gMapDetails?: TGMapLocation;
  };

  export type TPinIcon = {
    facilityAddress?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
  };

  export interface IconTree {
    tag: string;
    attr: {
      [key: string]: string;
    };
    child: IconTree[];
  }
  export interface IconBaseProps extends React.SVGAttributes<SVGElement> {
    children?: React.ReactNode;
    size?: string | number;
    color?: string;
    title?: string;
  }
  export type IconType = (props: IconBaseProps) => JSX.Element;
  export type TGoogleMaps = typeof window.google.maps;
  export type TGoogleMap = typeof window.google.maps.Map;
  export type TMapLoaded = {
    map: any;
    maps: any;
  };
  export type TPlaceDetails = {
    name: string;
    placeId: string;
  };
  export type TVariantModes =
    | 'cover-image'
    | 'map-multi-1'
    | 'map-multi-3'
    | 'map-multi-locations';
  export interface IRating extends HTMLAttributes<HTMLDivElement> {
    rating: number;
    size?: number;
    fillColor?: string;
    maxRating?: number;
    totalReviews?: number;
  }
  export interface IGeolocationCoordinates {
    readonly accuracy: number;
    readonly altitude: number | null;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly latitude: number;
    readonly longitude: number;
    readonly speed: number | null;
  }

  export interface IGeolocationPosition {
    readonly coords: GeolocationCoordinates;
    readonly timestamp: number;
  }
  export interface IGeolocationPositionError {
    readonly code: number;
    readonly message: string;
    readonly PERMISSION_DENIED: number;
    readonly POSITION_UNAVAILABLE: number;
    readonly TIMEOUT: number;
  }

  export type TDestinationConfig = {
    coords: TCoords;
    placeId: string;
  };

  export type TMapUrlConfig = {
    origin: TCoords;
    destination: TDestinationConfig;
  };
}
